import React, { SyntheticEvent } from 'react'

import styled from '@emotion/styled'
import {
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  JustifyContent,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { useGetAlternativeOptionsForRecipeHook, useRecipe, useStockHook } from '../../model/context'
import { useRecipeReference } from '../../model/context/useRecipeReference'
import { CookingTimeText } from '../CookingTimeText/CookingTimeText'
import { RecipeTilePurchaseInfo } from '../RecipeTilePurchaseInfo'
import { TileImage } from '../TileImage/TileImage'
import { Title } from '../Title'
import {
  cssAddRecipeButtonContainer,
  cssRecipeTagHolder,
  cssRecipeTile,
  cssRecipeTileContainer,
  cssRecipeTileIsFineDineIn,
} from './styles'
import { AddRecipeButton } from '../AddRecipeButton'
import { DietaryLabels } from '../DietaryLabels/DietaryLabels'
import { LabelWrapper } from '../Label/LabelWrapper'
import { SoldOutOverlay } from '../SoldOutOverlay'
import { SwapAlternativeOptions } from '../SwapAlternativeOptions/SwapAlternativeOptions'

const OuterContainer = styled.div(cssRecipeTile)

const TileContainer = styled.div((props: any) => ({
  ...cssRecipeTileContainer,
  ...(props.isFineDineIn ? cssRecipeTileIsFineDineIn : {}),
}))

const RecipeTagContainer = styled.span(() => ({
  ...cssRecipeTagHolder,
}))

const AddRecipeButtonContainer = styled.span((props: any) => ({
  ...cssAddRecipeButtonContainer,
  ...(props.isFineDineIn ? cssRecipeTileIsFineDineIn : {}),
}))

const ImageContainer = withCitrusTheme(Box, () => ({
  minHeight: '200px',
  maxHeight: '270px',
  position: 'relative',
  overflow: 'hidden',
}))

export type RecipeTileProps = {
  originalId: string
  currentCollectionId: string
  onClick: (recipeId: string, currentCollectionId: string, recipeReference?: string | null) => void
  SwapAlternativeOptionsMobile?: React.FC<Record<string, unknown>>
}

export const RecipeTile = ({
  originalId,
  currentCollectionId: categoryId,
  onClick,
  SwapAlternativeOptionsMobile,
}: RecipeTileProps) => {
  const useStock = useStockHook()
  const { isRecipeOutOfStock } = useStock()

  const { id: recipeId, isFineDineIn } = useRecipe()
  const isOutOfStock = isRecipeOutOfStock(recipeId)

  const recipeReference = useRecipeReference()

  const useGetAlternativeOptionsForRecipe = useGetAlternativeOptionsForRecipeHook()
  const getAlternativeOptionsForRecipe = useGetAlternativeOptionsForRecipe()

  // should never happen but caters for loading state
  if (categoryId === null) {
    return null
  }

  const alternatives = getAlternativeOptionsForRecipe({
    recipeId,
    categoryId,
    isOnDetailScreen: false,
  })
  // alternative options include the recipe itself
  const hasAlternativeOptions = alternatives.length > 1

  const handleOnClick = (e: SyntheticEvent) => {
    e.stopPropagation()
    onClick(recipeId, categoryId, recipeReference)
  }

  const handleOnClickRecipeButtonContainer = (e: SyntheticEvent) => {
    // Prevents opening recipe details modal when clicking on disabled add button
    if (isOutOfStock) e.stopPropagation()
  }

  const overlayBgColor = isFineDineIn ? Color.ColdGrey_800 : Color.White

  return (
    <OuterContainer
      role="button"
      tabIndex={0}
      data-testing={isOutOfStock ? 'menuRecipeOutOfStock' : 'menuRecipeViewDetails'}
      data-testing-id={recipeId}
      onClick={handleOnClick}
      onKeyPress={handleOnClick}
    >
      <TileContainer isFineDineIn={isFineDineIn}>
        <ImageContainer>
          <SoldOutOverlay />
          <TileImage />
          <RecipeTagContainer>
            <DietaryLabels />
          </RecipeTagContainer>
        </ImageContainer>

        <Box position={DisplayPosition.Relative} flexGrow={1} style={{ margin: '1rem' }}>
          <Box display={Display.Flex}>
            <LabelWrapper />
            <AddRecipeButtonContainer
              isFineDineIn={isFineDineIn}
              isSoldOut={isOutOfStock}
              onClick={handleOnClickRecipeButtonContainer}
            >
              <AddRecipeButton recipeId={recipeId} />
            </AddRecipeButtonContainer>
          </Box>
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            justifyContent={JustifyContent.SpaceBetween}
            height="100%"
            position={DisplayPosition.Relative}
          >
            <SoldOutOverlay backgroundColor={overlayBgColor} />
            <Box>
              <Title />
              <Space size={2}></Space>
              <CookingTimeText />
            </Box>
            <RecipeTilePurchaseInfo />
          </Box>
          {hasAlternativeOptions && (
            <Box position={DisplayPosition.Absolute} bottom="0" right="0" width="100%">
              <SwapAlternativeOptions
                originalId={originalId}
                recipeId={recipeId}
                categoryId={categoryId}
                SwapAlternativeOptionsMobile={SwapAlternativeOptionsMobile}
              />
            </Box>
          )}
        </Box>
      </TileContainer>
    </OuterContainer>
  )
}
